import {ClassTransformer} from 'class-transformer';
import * as Keycloak from 'keycloak-js';
import {GlobalPermissionId, PermissionId} from '@app/layout/admin/services/api/permission-interfaces';
import {NonPersistedUserPreferences, UserPreferences} from '..';
import {User, UserImpl} from '@app/layout/admin/services/api/user-interfaces';
import {UserEntity, UserEntityType, BaseUserEntity} from '@app/shared/services/api/user-entity';

export interface LoggedInUser {
  userId: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  userEntity: PermissionedUserEntity;
  license: License;
  preferences: UserPreferences;
  nonPersistedPreferences: NonPersistedUserPreferences;
  overrideUserEntity: BaseUserEntity | null;

  isLicenser(): boolean;

  isCustomerUser(): boolean;

  isClientUser(): boolean;

  isContractorUser(): boolean;

  isPlatformAdmin(): boolean;

  isResourceSharer(): boolean;

  isAdminUser(): boolean;

  isCustomerUserOrPlatformAdmin(): boolean;

  isCustomerAdmin(): boolean;

  isCustomerClientAdmin(): boolean;

  isClientAdmin(): boolean;

  /**
   * @deprecated, use explicit permission methods: isCustomerAdmin, isCustomerClientAdmin, isClientAdmin
   * @param permissionId
   */
  hasPermissionOnUserEntity(permissionId: PermissionId): boolean;

  asUser(): User;
}

export interface PermissionedUserEntity extends UserEntity {
  permissions?: PermissionId[];
}

export interface LicenseProperties {
  energy_module: boolean;
  thermal_comfort_module: boolean;
  maintenance_module: boolean;
}

export interface License extends LicenseProperties {
  isLicensed(): boolean;
}

export class KeycloakLoggedInUser implements LoggedInUser {
  constructor(public readonly userId: string,
              public readonly userEntity: PermissionedUserEntity,
              public readonly license: License,
              public readonly preferences: UserPreferences,
              public readonly overrideUserEntity: BaseUserEntity | null,
              public nonPersistedPreferences: NonPersistedUserPreferences,
              private readonly keycloakProfile: Keycloak.KeycloakProfile,
              private readonly globalPermissions: GlobalPermissionId[],
              private readonly classTransformer: ClassTransformer) {
  }

  get firstName(): string {
    return this.keycloakProfile.firstName;
  }

  get lastName(): string {
    return this.keycloakProfile.lastName;
  }

  get username(): string {
    return this.keycloakProfile.username;
  }

  get email(): string {
    return this.keycloakProfile.email;
  }

  isCustomerUser(): boolean {
    return this.userEntity && this.userEntity.type === UserEntityType.CUSTOMER;
  }

  isClientUser(): boolean {
    return this.userEntity && this.userEntity.type === UserEntityType.CLIENT;
  }

  isContractorUser(): boolean {
    return this.userEntity && this.userEntity.type === UserEntityType.CONTRACTOR;
  }

  isPlatformAdmin(): boolean {
    return this.globalPermissions.includes(GlobalPermissionId.PLATFORM_ADMIN);
  }

  isResourceSharer(): boolean {
    return this.globalPermissions.includes(GlobalPermissionId.RESOURCE_SHARER);
  }

  isLicenser(): boolean {
    return this.globalPermissions.includes(GlobalPermissionId.LICENSER);
  }

  isAdminUser(): boolean {
    return this.isPlatformAdmin() || this.isCustomerAdmin() || this.isCustomerClientAdmin() || this.isClientAdmin();
  }

  isCustomerUserOrPlatformAdmin(): boolean {
    return this.isPlatformAdmin() || this.isCustomerUser();
  }

  isClientAdmin(): boolean {
    return this.hasPermissionOnUserEntity(PermissionId.CLIENT_ADMIN);
  }

  isCustomerAdmin(): boolean {
    return this.hasPermissionOnUserEntity(PermissionId.CUSTOMER_ADMIN);
  }

  isCustomerClientAdmin(): boolean {
    return this.hasPermissionOnUserEntity(PermissionId.CUSTOMER_CLIENT_ADMIN);
  }

  hasPermissionOnUserEntity(permissionId: PermissionId): boolean {
    return this.userEntity?.permissions?.includes(permissionId) ?? false;
  }

  asUser(): User {
    const user = this.classTransformer.plainToInstance(UserImpl, {
      id: this.userId,
      username: this.username,
      firstname: this.firstName,
      lastname: this.lastName,
      email: this.email,
      ...this.isClientUser() && { client_id: this.userEntity.id },
      ...this.isContractorUser() && { contractor_id: this.userEntity.id },
      ...this.isCustomerUser() && { customer_id: this.userEntity.id },
    });
    // do not put entity through plainToClass
    // breaks if entity has already been transformed to UserEntityImpl
    // as it would not be plainToClass transformation
    user.entity = this.userEntity;
    return user;
  }
}
